import { useQuery } from '@tanstack/react-query'
import request, { authOptions } from 'api/requestUtils'
import type { definitions } from '@luxuryescapes/contract-svc-promo'
import { mapCompetitionConfig, mapCompetitionMinorPrize, mapCompetitionUserStatus, mapContentfulWebPageData } from './mapping'
import type { ContentfulWebPageResponseFields } from './mapping'
import type { DestinationLandingPageType } from 'components/Pages/DestinationLandingPage/DestinationLandingPageUtils'
import type { paths } from '@luxuryescapes/contract-svc-public-offer-feed'
import type { PublicOfferV2 } from '@luxuryescapes/contract-public-offer'
import { mapServerOfferToAppOffer } from 'api/offer'
import qs from 'qs'
import config from 'constants/config'

export async function getReferralValue(region: string) {
  return request.get<App.ApiResponse<App.ReferralValue>>(`/api/referral/value?regionCode=${region}`)
}

export async function getReferralLogs(accessToken?: string) {
  return request.get<App.ApiResponse<App.ReferralLogs>>('/api/referral/logs', authOptions(accessToken))
}

export async function getEarnOptions(region: string) {
  return request.get<App.ApiResponse<{ earn_options: Array<App.EarnOption> }>>(`/api/referral/earn-options?region=${region}`)
}

export async function getNblCompetitionPrize() {
  return request.get('/api/promo/competition/nblCompetition/entry', { credentials: 'include' })
}

export async function getNblCompetitionObject() {
  return request.get('/api/promo/competition/nblCompetition/config')
}

export async function getNblCompetitionUserStatus(accessToken?: string) {
  return request.get('/api/promo/competition/nblCompetition/check?', authOptions(accessToken))
}

export async function getAuCompetitionPrize() {
  return request.get('/api/promo/competition/auCompetition/entry?', { credentials: 'include' })
}

export async function getAuCompetitionObject() {
  return request.get('/api/promo/competition/auCompetition/config')
}

export async function getAuCompetitionUserStatus(region?: string, accessToken?: string) {
  return request.get(`/api/promo/competition/auCompetition/check?region=${region}`, authOptions(accessToken))
}

export async function getUkCompetitionPrize() {
  return request.get('/api/promo/competition/ukCompetition/entry', { credentials: 'include' })
}

export async function getUkCompetitionObject() {
  return request.get('/api/promo/competition/ukCompetition/config')
}

export async function getUkCompetitionUserStatus(accessToken?: string) {
  return request.get('/api/promo/competition/ukCompetition/check', authOptions(accessToken))
}

export async function getIntlCompetitionPrize(region?: string) {
  return request.get(`/api/promo/competition/intlCompetition/entry?region=${region}`, { credentials: 'include' })
}

export async function getIntlCompetitionObject(region?: string) {
  return request.get(`/api/promo/competition/intlCompetition/config?region=${region}`)
}

export async function getIntlCompetitionUserStatus(region?: string, accessToken?: string) {
  return request.get(`/api/promo/competition/intlCompetition/check?region=${region}`, authOptions(accessToken))
}

export async function getCompetitionPrize(region?: string, competitionName?: string) {
  return request.get(`/api/promo/competition/${competitionName}/entry?region=${region}`, { credentials: 'include' })
}

export async function getCompetitionObject(region?: string, competitionName?: string) {
  return request.get(`/api/promo/competition/${competitionName}/config?region=${region}`)
}

export async function getCompetitionUserStatus(region?: string, competitionName?: string, accessToken?: string) {
  return request.get(`/api/promo/competition/${competitionName}/check?region=${region}`, authOptions(accessToken))
}

export async function getDestinationPage(destination: string) {
  return request.get(`/api/content/pageDestinationDiscover/${destination}`)
}

export async function getContentfulWebPage(path: string, regionCode: string, locale?: string) {
  const query = qs.stringify({ slug: path, regionCode, locale })
  const url = `/api/content/web-page?${query}`

  return locale ? request.get(url, { headers: { 'Accept-Language': locale } }) : request.get(url)
}

export function selectContentfulWebPageData(response: { result: Array<{ fields: ContentfulWebPageResponseFields }> }): App.ContentfulWebPageData | undefined {
  const fields = response.result?.[0]?.fields
  return fields ? mapContentfulWebPageData(fields) : undefined
}

type GetForcedRankingOffersResponse = paths['/api/public-offer-feed/forced-rankings-offer']['get']['responses']['200']['content']['application/json']['result']
export async function getForcedRankingOffers(region: string, latitude: number, longitude: number, offerCountry: string, limit = 5): Promise<App.ApiResponse<GetForcedRankingOffersResponse>> {
  return request.get(`/api/public-offer-feed/forced-rankings-offer?country_code=${offerCountry}&latitude=${latitude}&longitude=${longitude}&user_region=${region}&limit=${limit}`, { excludeBrand: true })
}

type GetMetaOfferIdsParams = paths['/api/public-offer-feed/meta-offers-ids']['get']['parameters']['query']
type GetMetaOfferIdsResponse = paths['/api/public-offer-feed/meta-offers-ids']['get']['responses']['200']['content']['application/json']

export async function getMetaOfferIds(params: GetMetaOfferIdsParams): Promise<GetMetaOfferIdsResponse> {
  const queryString = qs.stringify(params)
  return request.get<GetMetaOfferIdsResponse>(`/api/public-offer-feed/meta-offers-ids?${queryString}`, { credentials: 'include' })
}

export type GetMetaOffersDiscountParams = Omit<paths['/api/public-offer-feed/offer-discount/']['get']['parameters']['query'], 'brand'>
type GetMetaOffersDiscountResponse = paths['/api/public-offer-feed/offer-discount/']['get']['responses']['200']['content']['application/json']['result']
export async function getOfferDiscount(params: GetMetaOffersDiscountParams) {
  return request.get<App.ApiResponse<GetMetaOffersDiscountResponse>>(`/api/public-offer-feed/offer-discount?${qs.stringify(params)}`, { credentials: 'include' })
    .then((response) => {
      return response.result
    })
}

export type GetOfferWithDiscountParams = paths['/api/public-offer-feed/{offerId}']['get']['parameters']['query']
type GetOfferWithDiscountResponse = paths['/api/public-offer-feed/{offerId}']['get']['responses']['200']['content']['application/json']['result']

type DiscountedPackage = NonNullable<GetOfferWithDiscountResponse['discountedPackages']>[number]

interface OfferWithDiscountResult {
  offer?: App.Offer | App.BedbankOffer | App.Tours.TourV2OfferSummary
  discountedPackages?: Array<DiscountedPackage>
}

export async function getOfferWithDiscount(
  offerId: string,
  params: GetOfferWithDiscountParams,
  regionCode: string,
): Promise<OfferWithDiscountResult> {
  return request.get<App.ApiResponse<GetOfferWithDiscountResponse>>(
    `/api/public-offer-feed/${offerId}?${qs.stringify(params)}`,
    { credentials: 'include' },
  ).then((response) => {
    const serverOffer = response.result.offer as PublicOfferV2.Offer
    const mappedOffer = mapServerOfferToAppOffer(serverOffer, regionCode)
    return {
      offer: mappedOffer,
      discountedPackages: response.result.discountedPackages,
    }
  })
}

export function generalLog({ status, message, payload }: Omit<paths['/api/public-offer-feed/log']['post']['requestBody']['content']['application/json'], 'brand'>) {
  return request.post('/api/public-offer-feed/log', {
    status,
    message,
    payload,
  })
}

type GetPlaceSiteLinksResponse = paths['/api/public-offer-feed/v1/sitelink/{pageType}/{placeName}']['get']['responses']['200']['content']['application/json']
export async function getPlaceSitelinks(
  { pageType, placeName }: { pageType: DestinationLandingPageType, placeName: string }): Promise<GetPlaceSiteLinksResponse> {
  return request.get(`/api/public-offer-feed/v1/sitelink/${pageType}/${placeName.toLowerCase()}`)
    .then((response) => {
      return response as GetPlaceSiteLinksResponse
    })
}

export function useCompetitionConfig(slug: string, region?: string) {
  return useQuery<any, { errors: Array<string> }>({
    queryKey: [`competition-config-${slug}`],
    queryFn: () => getCompetitionObject(region, slug),
    select: (response: { result: definitions['CompetitionConfigV2'] }): App.MarketingCompetitionConfig => {
      return mapCompetitionConfig(response.result)
    },
  })
}

export function useCompetitionPrize(competitionName?: string, episodeId = 1, region?: string) {
  return useQuery({
    queryKey: [`competition-prize-${competitionName}-${episodeId}`],
    queryFn: () => getCompetitionPrize(region, competitionName),
    enabled: false,
    select: (response: any): App.MarketingCompetitionMinorPrize => {
      return mapCompetitionMinorPrize(response)
    },
  })
}

export function useCompetitionStatus(competitionName?: string, episode = 1, region?: string) {
  return useQuery({
    queryKey: [`competition-status-${competitionName}-${episode}`],
    queryFn: () => getCompetitionUserStatus(region, competitionName),
    select: (response: any): App.MarketingCompetitionUserStatus => {
      return mapCompetitionUserStatus(response)
    },
  })
}

export function useContentfulWebPage(path: string, regionCode: string, locale?: string) {
  return useQuery({
    queryKey: ['contentful-web-page', path, regionCode, locale],
    queryFn: () => getContentfulWebPage(path, regionCode, locale),
    select: selectContentfulWebPageData,
  })
}

export function useFetchMetaOffersDiscount(params: {
  offerId: string
  region: string
  checkIn: string
  checkOut: string
  occupancy: string
  enabled?: boolean
}) {
  const { offerId, region, checkIn, checkOut, occupancy, enabled = true } = params

  // Check for meta offers cookie
  const hasMetaOffersCookie = typeof document !== 'undefined' && document?.cookie?.split('; ').some(cookie => cookie.startsWith('metaofferSID'))

  return useQuery({
    queryKey: ['meta-offers-discount', offerId, region, checkIn, checkOut, occupancy],
    queryFn: () => getOfferDiscount({
      offerId,
      region,
      checkIn,
      checkOut,
      occupancy,
    }),
    enabled: enabled && !!offerId && !!checkIn && !!checkOut && !!occupancy && hasMetaOffersCookie && config.ENABLE_META_OFFERS_DISCOUNT,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export type ValidateOfferDiscountParams = Omit<paths['/api/public-offer-feed/offer-discount/validate']['get']['parameters']['query'], 'brand'>
type ValidateOfferDiscountResponse = paths['/api/public-offer-feed/offer-discount/validate']['get']['responses']['200']['content']['application/json']
export async function validateOfferDiscount(params: ValidateOfferDiscountParams): Promise<string> {
  return request.get<ValidateOfferDiscountResponse>(`/api/public-offer-feed/offer-discount/validate?${qs.stringify(params)}`, { credentials: 'include' })
    .then((response) => {
      return response.result.promoCode
    })
}
